import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { servicesAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ChevronUp, ChevronDown, Copy } from 'lucide-react';
import axios from 'axios';
import SimpleRichTextEditor from '@/components/admin/SimpleRichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Verfügbare Platzhalter für die Vorab-Info (gespiegelt aus
// backend/services/pre_appointment_renderer.py::PLACEHOLDERS)
const PRE_INFO_PLACEHOLDERS = [
  { key: 'first_name', label: 'Vorname' },
  { key: 'last_name', label: 'Nachname' },
  { key: 'full_name', label: 'Voller Name' },
  { key: 'service_name', label: 'Service' },
  { key: 'appointment_date', label: 'Datum' },
  { key: 'appointment_time', label: 'Uhrzeit' },
  { key: 'appointment_datetime', label: 'Datum + Zeit' },
  { key: 'trainer_name', label: 'Trainer' },
  { key: 'studio_name', label: 'Studio' },
  { key: 'studio_address', label: 'Adresse' },
  { key: 'studio_phone', label: 'Telefon' },
  { key: 'days_before', label: 'Tage vorher' },
];

const ServicesPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const preInfoEditorRef = React.useRef(null);
  const [preInfoPreview, setPreInfoPreview] = useState(null);
  const [aiFormatting, setAiFormatting] = useState(false);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [totalServices, setTotalServices] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_minutes: 60,
    price: 0,
    points_cost: 0,
    max_participants: 1,
    category: '',
    color: '#FCD34D', // NEW: Default yellow
    tax_rate: 8.1,
    tax_included: true,
    is_online_bookable: true, // NEW: Online booking allowed
    is_training_service: true, // NEW: Counts as training for nutrition goals
    once_per_customer: false, // NEW: Only bookable once per customer
    is_trial: false, // NEW 09/2026: Explizit als Probetraining markiert
    include_in_service_abo: true, // 07/2026: Im „Service-Abo selbst kombinieren"-Widget anbieten?
    push_to_mindbody: false, // NEW: Push appointments to Mindbody as "Reserved"
    is_ems: false, // 06/2026: EMS-Training – Anzug vorbereiten
    is_physio_session: false, // 06/2026: zählt als Verordnungssitzung (Physio)
    ems_variants: [], // 06/2026: EMS pro Variante
    studio_ids: [], // NEW: Multi-Studio - service available in these studios
    trainer_ids: [], // NEW: Zugeordnete Trainer (u.a. für Probetraining-Follow-Up)
    variants: [], // NEW: Service variants (e.g., ["EMS", "Personal Training"])
    // Service-specific reminders
    reminder_enabled: false,
    reminder_hours_before: 12,
    reminder_email: true,
    reminder_whatsapp: true,
    pre_info_enabled: false,
    pre_info_days_before: 3,
    pre_info_content: '',
    pre_info_wa_template_name: '',
    pre_info_wa_template_language: 'de',
    pre_info_wa_template_params: [],
    // Warmup time for add-on services
    warmup_time_minutes: 0,
    // Info-Link für Customer-Popup (z. B. zur StrongFit-Seite)
    info_url: '',
    // Vertical/Beauty: Aufpreis/Zusatzzeit, Mitarbeiter-Preise, Einwirkzeit-Phasen
    variant_options: [],
    staff_pricing: [],
    treatment_phases: [],
    has_treatment_phases: false,
    resource_requirements: [], // Block C: benötigte Ressourcen (fix ODER nach Typ)
  });
  
  // State for variant input
  const [newVariant, setNewVariant] = useState('');

  // 06/2026: Email-Vorlagen für Vorab-Info-Dropdown laden
  const [preInfoEmailTemplates, setPreInfoEmailTemplates] = useState([]);
  
  const [taxSettings, setTaxSettings] = useState({
    default_tax_rate: 8.1,
    tax_included_in_prices: true,
    show_tax_breakdown: true
  });

  useEffect(() => {
    fetchTaxSettings();
    fetchCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, categoryFilter]);

  useEffect(() => {
    setSelectedServiceIds(new Set());
  }, [currentPage, debouncedSearch, categoryFilter]);

  useEffect(() => {
    fetchServices();
  }, [currentPage, debouncedSearch, categoryFilter]);

  // Approved WhatsApp-Templates (für Vorab-Info Template-Selector)
  const [waTemplates, setWaTemplates] = useState([]);
  // Multi-Studio: alle Studios für Auswahl im Service-Formular + Filter
  const [studios, setStudios] = useState([]);
  const [studioFilter, setStudioFilter] = useState('all');
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/studios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudios(Array.isArray(data) ? data : []);
      } catch (e) {
        // silently fail — fallback: keine Multi-Studio-Auswahl
      }
    })();
  }, []);
  // Block C: Ressourcen (Räume/Geräte) für Ressourcen-Anforderungen der Leistung
  const [availableResources, setAvailableResources] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/resources`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAvailableResources(Array.isArray(data) ? data : []);
      } catch (e) {
        // silently fail
      }
    })();
  }, []);
  // Branchen-Features (Vertical Gating): steuert, welche Beauty/Physio-Felder sichtbar sind
  const [verticalFeatures, setVerticalFeatures] = useState({ variant_pricing: true, staff_pricing: false, treatment_phases: false });
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/settings/vertical-features`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data?.features) setVerticalFeatures(data.features);
      } catch (e) {
        // defaults bleiben bestehen
      }
    })();
  }, []);
  // Seed-Dialog: erlaubt Editieren des Vorab-Info-Templates VOR dem Submit
  const [seedDialog, setSeedDialog] = useState(null);
  const [waFormatting, setWaFormatting] = useState(false);
  // Trainer für Service-Zuordnung (u.a. Probetraining-Follow-Up)
  const [trainers, setTrainers] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        // Trainer-Liste = Trainer UND Admins: Admins arbeiten teils selbst als
        // Trainer und haben eine eigene Kalender-Spalte. Muss konsistent zu den
        // Kalender-Spalten sein (/trainers/availability/all filtert role in
        // [trainer, admin]) — sonst kann ein Admin keinem Service zugeordnet
        // werden und die eingeschränkten Services verschwinden aus seiner Spalte.
        const [tRes, aRes] = await Promise.all([
          axios.get(`${API}/users`, { params: { role: 'trainer' }, headers }),
          axios.get(`${API}/users`, { params: { role: 'admin' }, headers }),
        ]);
        const norm = (d) => (Array.isArray(d) ? d : (d?.items || []));
        const merged = [...norm(tRes.data), ...norm(aRes.data)].filter(
          (u) => u && u.is_active !== false
        );
        const seen = new Set();
        const unique = merged.filter((u) =>
          seen.has(u.id) ? false : seen.add(u.id)
        );
        setTrainers(unique);
      } catch (e) {
        // silently fail — keine Trainer-Zuordnung möglich
      }
    })();
  }, []);
  useEffect(() => {
    if (!dialogOpen) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${API}/whatsapp/templates?status=APPROVED&limit=200`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setWaTemplates(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        // Wenn WhatsApp nicht konfiguriert ist, einfach leere Liste lassen
        setWaTemplates([]);
      }
    })();
  }, [dialogOpen]);

  // 06/2026: Email-Vorlagen mit Kategorie `pre_info` für Dropdown laden
  useEffect(() => {
    if (!dialogOpen) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${API}/email/templates`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const list = Array.isArray(data) ? data : (data?.templates || []);
        // Nur Templates die als Pre-Info markiert sind ODER explizit category=pre_info haben
        const filtered = list.filter(
          (t) => t?.is_pre_info === true || t?.category === 'pre_info'
        );
        setPreInfoEmailTemplates(filtered);
      } catch (e) {
        setPreInfoEmailTemplates([]);
      }
    })();
  }, [dialogOpen]);

  // Body-Variablen aus einem ausgewählten Template extrahieren ({{1}}…{{n}})
  const getTemplateBodyVariableCount = (templateName) => {
    const tpl = waTemplates.find((t) => t.name === templateName);
    if (!tpl) return 0;
    const body = (tpl.components || []).find((c) => c.type === 'BODY');
    if (!body?.text) return 0;
    const matches = body.text.match(/\{\{\s*(\d+)\s*\}\}/g) || [];
    const idxs = matches.map((m) => parseInt(m.replace(/[^0-9]/g, ''), 10));
    return idxs.length ? Math.max(...idxs) : 0;
  };

  const getTemplateBodyPreview = (templateName) => {
    const tpl = waTemplates.find((t) => t.name === templateName);
    const body = (tpl?.components || []).find((c) => c.type === 'BODY');
    return body?.text || '';
  };
  
  const fetchTaxSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/settings/tax`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTaxSettings(data);
    } catch (error) {
      console.error('Error fetching tax settings:', error);
    }
  };

  // Helper function to calculate price display
  const calculatePriceDisplay = (service) => {
    const price = service.price;
    const taxRate = service.tax_rate || 8.1;
    const taxIncluded = service.tax_included !== undefined ? service.tax_included : true;

    if (!taxSettings.show_tax_breakdown) {
      return `CHF ${price.toFixed(2)}`;
    }

    if (taxIncluded) {
      // Price includes tax, calculate net price
      const netPrice = price / (1 + taxRate / 100);
      const taxAmount = price - netPrice;
      return (
        <div className="text-sm">
          <div className="font-medium">CHF {price.toFixed(2)}</div>
          <div className="text-xs text-gray-500">
            (Netto: CHF {netPrice.toFixed(2)} + MWST {taxRate}%: CHF {taxAmount.toFixed(2)})
          </div>
        </div>
      );
    } else {
      // Price excludes tax, calculate gross price
      const taxAmount = price * (taxRate / 100);
      const grossPrice = price + taxAmount;
      return (
        <div className="text-sm">
          <div className="text-gray-500">CHF {price.toFixed(2)} + MWST</div>
          <div className="font-medium">CHF {grossPrice.toFixed(2)}</div>
          <div className="text-xs text-gray-500">
            (inkl. {taxRate}% MWST: CHF {taxAmount.toFixed(2)})
          </div>
        </div>
      );
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: pageSize,
        sort_field: 'order',
        sort_dir: 'asc'
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }

      const response = await servicesAPI.listPaginated(params);
      setServices(response.data.items || []);
      setTotalServices(response.data.total || 0);
    } catch (error) {
      toast.error('Fehler beim Laden der Services');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const response = await fetch(`${API}/service-categories`, { headers });
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Kategorien');
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Bitte Kategorie-Namen eingeben');
      return;
    }

    try {
      const response = await fetch(`${API}/service-categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim Erstellen');
      }

      toast.success('Kategorie erstellt!');
      setNewCategoryName('');
      setNewCategoryDialogOpen(false);
      await fetchCategories();

      setFormData((prev) => ({ ...prev, category: data.name }));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleMoveService = async (serviceId, direction) => {
    try {
      const response = await fetch(`${API}/services/${serviceId}/move`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ direction })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Verschieben');
      }

      toast.success(`Service nach ${direction === 'up' ? 'oben' : 'unten'} verschoben`);
      await fetchServices(); // Reload services
    } catch (error) {
      toast.error(error.message || 'Fehler beim Verschieben');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Block C: leere Ressourcen-Zeilen (weder feste ID noch Typ) herausfiltern
      const cleanReqs = (formData.resource_requirements || []).filter(
        (r) => (r.resource_id && r.resource_id.trim()) || (r.resource_type && r.resource_type.trim())
      );
      const payload = { ...formData, resource_requirements: cleanReqs };
      if (editMode) {
        await servicesAPI.update(editingId, payload);
        toast.success('Service aktualisiert!');
      } else {
        await servicesAPI.create(payload);
        toast.success('Service erstellt!');
      }
      setDialogOpen(false);
      fetchServices();
      resetForm();
    } catch (error) {
      toast.error(editMode ? 'Fehler beim Aktualisieren' : 'Fehler beim Erstellen');
    }
  };

  const handleEdit = (service) => {
    setEditMode(true);
    setEditingId(service.id);
    setFormData({
      name: service.name,
      description: service.description || '',
      duration_minutes: service.duration_minutes,
      price: service.price,
      points_cost: service.points_cost || 0,
      max_participants: service.max_participants || 1,
      category: service.category || '',
      color: service.color || '#FCD34D',
      order: service.order || 0,
      tax_rate: service.tax_rate || taxSettings.default_tax_rate,
      tax_included: service.tax_included !== undefined ? service.tax_included : taxSettings.tax_included_in_prices,
      is_online_bookable: service.is_online_bookable !== false,
      is_training_service: service.is_training_service !== false,
      once_per_customer: service.once_per_customer || false,
      is_trial: service.is_trial || false,
      include_in_service_abo: service.include_in_service_abo !== false,
      push_to_mindbody: !!service.push_to_mindbody,
      is_ems: !!service.is_ems,
      is_physio_session: !!service.is_physio_session,
      ems_variants: service.ems_variants || [],
      studio_ids: service.studio_ids && service.studio_ids.length > 0
        ? service.studio_ids
        : (service.studio_id ? [service.studio_id] : []),
      trainer_ids: service.trainer_ids || [],
      variants: service.variants || [],
      // Service-specific reminders
      reminder_enabled: service.reminder_enabled || false,
      reminder_hours_before: service.reminder_hours_before || 12,
      reminder_email: service.reminder_email !== false,
      reminder_whatsapp: service.reminder_whatsapp !== false,
      pre_info_enabled: service.pre_info_enabled || false,
      pre_info_days_before: service.pre_info_days_before || 3,
      pre_info_content: service.pre_info_content || '',
      pre_info_wa_template_name: service.pre_info_wa_template_name || '',
      pre_info_wa_template_language: service.pre_info_wa_template_language || 'de',
      pre_info_wa_template_params: service.pre_info_wa_template_params || [],
      warmup_time_minutes: service.warmup_time_minutes || 0,
      info_url: service.info_url || '',
      variant_options: service.variant_options || [],
      staff_pricing: service.staff_pricing || [],
      treatment_phases: service.treatment_phases || [],
      has_treatment_phases: service.has_treatment_phases || false,
      resource_requirements: service.resource_requirements || [],
    });
    setNewVariant('');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Service wirklich löschen?')) {
      try {
        await servicesAPI.delete(id);
        toast.success('Service gelöscht');
        if (services.length === 1 && currentPage > 1) {
          setCurrentPage((prev) => Math.max(1, prev - 1));
        } else {
          fetchServices();
        }
      } catch (error) {
        toast.error('Fehler beim Löschen');
      }
    }
  };

  // --- Vertical/Beauty Helpers ---
  const getVO = (name) => (formData.variant_options || []).find((v) => v.name === name) || { name, price_delta: 0, duration_delta_minutes: 0 };
  const setVO = (name, patch) => {
    const list = [...(formData.variant_options || [])];
    const idx = list.findIndex((v) => v.name === name);
    const base = idx >= 0 ? list[idx] : { name, price_delta: 0, duration_delta_minutes: 0, ems: false };
    const updated = { ...base, ...patch, name };
    if (idx >= 0) list[idx] = updated; else list.push(updated);
    setFormData({ ...formData, variant_options: list });
  };
  const getSP = (tid) => (formData.staff_pricing || []).find((s) => s.trainer_id === tid) || { trainer_id: tid, price: null, duration_minutes: null };
  const setSP = (tid, patch) => {
    const list = [...(formData.staff_pricing || [])];
    const idx = list.findIndex((s) => s.trainer_id === tid);
    const base = idx >= 0 ? list[idx] : { trainer_id: tid, price: null, duration_minutes: null };
    const updated = { ...base, ...patch, trainer_id: tid };
    if (idx >= 0) list[idx] = updated; else list.push(updated);
    setFormData({ ...formData, staff_pricing: list });
  };
  const addPhase = () => setFormData({ ...formData, treatment_phases: [...(formData.treatment_phases || []), { name: '', duration_minutes: 10, staff_busy: true }] });
  const updatePhase = (idx, patch) => {
    const list = [...(formData.treatment_phases || [])];
    list[idx] = { ...list[idx], ...patch };
    setFormData({ ...formData, treatment_phases: list });
  };
  const removePhase = (idx) => setFormData({ ...formData, treatment_phases: (formData.treatment_phases || []).filter((_, i) => i !== idx) });
  const phasesTotal = (formData.treatment_phases || []).reduce((s, p) => s + (parseInt(p.duration_minutes) || 0), 0);
  // Block C: Ressourcen-Anforderungen (fix ODER nach Typ)
  const addResourceReq = (req) => setFormData((prev) => ({ ...prev, resource_requirements: [...(prev.resource_requirements || []), req] }));
  const updateResourceReq = (idx, patch) => setFormData((prev) => {
    const list = [...(prev.resource_requirements || [])];
    list[idx] = { ...list[idx], ...patch };
    return { ...prev, resource_requirements: list };
  });
  const removeResourceReq = (idx) => setFormData((prev) => ({ ...prev, resource_requirements: (prev.resource_requirements || []).filter((_, i) => i !== idx) }));
  const resourceTypes = Array.from(new Set((availableResources || []).map((r) => r.type).filter(Boolean)));
  const resourceTypeLabel = (t) => ({ room: 'Raum', device: 'Gerät', equipment: 'Gerät', seat: 'Platz', station: 'Station' }[(t || '').toLowerCase()] || t || 'Ressource');
  // Fertige Behandlungsvorlagen (aktiv–Einwirken–aktiv) für 1-Klick-Einsatz.
  const PHASE_TEMPLATES = [
    { label: 'Färben 15/20/15', phases: [
      { name: 'Auftragen', duration_minutes: 15, staff_busy: true },
      { name: 'Einwirken', duration_minutes: 20, staff_busy: false },
      { name: 'Ausspülen & Föhnen', duration_minutes: 15, staff_busy: true } ] },
    { label: 'Tönung 10/15/10', phases: [
      { name: 'Auftragen', duration_minutes: 10, staff_busy: true },
      { name: 'Einwirken', duration_minutes: 15, staff_busy: false },
      { name: 'Ausspülen', duration_minutes: 10, staff_busy: true } ] },
    { label: 'Dauerwelle 20/15/15', phases: [
      { name: 'Wickeln', duration_minutes: 20, staff_busy: true },
      { name: 'Einwirken', duration_minutes: 15, staff_busy: false },
      { name: 'Fixieren', duration_minutes: 15, staff_busy: true } ] },
    { label: 'Strähnen 30/25/20', phases: [
      { name: 'Auftragen', duration_minutes: 30, staff_busy: true },
      { name: 'Einwirken', duration_minutes: 25, staff_busy: false },
      { name: 'Ausspülen & Styling', duration_minutes: 20, staff_busy: true } ] },
  ];
  const applyPhaseTemplate = (tpl) => setFormData({
    ...formData,
    has_treatment_phases: true,
    treatment_phases: tpl.phases.map((p) => ({ ...p })),
  });

  const resetForm = () => {
    setEditMode(false);
    setEditingId(null);
    setNewVariant('');
    setFormData({
      name: '',
      description: '',
      duration_minutes: 60,
      price: 0,
      points_cost: 0,
      max_participants: 1,
      category: '',
      color: '#FCD34D',
      tax_rate: taxSettings.default_tax_rate,
      tax_included: taxSettings.tax_included_in_prices,
      is_online_bookable: true,
      is_training_service: true,
      once_per_customer: false,
      is_trial: false,
      include_in_service_abo: true,
      push_to_mindbody: false,
      is_ems: false,
      is_physio_session: false,
      ems_variants: [],
      studio_ids: [],
      variants: [],
      // Service-specific reminders
      reminder_enabled: false,
      reminder_hours_before: 12,
      reminder_email: true,
      reminder_whatsapp: true,
      pre_info_enabled: false,
      pre_info_days_before: 3,
      pre_info_content: '',
      pre_info_wa_template_name: '',
      pre_info_wa_template_language: 'de',
      pre_info_wa_template_params: [],
      warmup_time_minutes: 0,
      info_url: '',
      variant_options: [],
      staff_pricing: [],
      treatment_phases: [],
      has_treatment_phases: false,
      resource_requirements: [],
    });
  };
  
  // Variant management functions
  const addVariant = () => {
    if (newVariant.trim() && !formData.variants.includes(newVariant.trim())) {
      setFormData({ ...formData, variants: [...formData.variants, newVariant.trim()] });
      setNewVariant('');
    }
  };
  
  const removeVariant = (variantToRemove) => {
    setFormData({ ...formData, variants: formData.variants.filter(v => v !== variantToRemove) });
  };

  const selectedCount = selectedServiceIds.size;
  const isAllSelected = services.length > 0 && services.every((service) => selectedServiceIds.has(service.id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedServiceIds(new Set());
    } else {
      setSelectedServiceIds(new Set(services.map((service) => service.id)));
    }
  };

  const toggleServiceSelection = (serviceId) => {
    setSelectedServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedServiceIds(new Set());
  };

  const handleBulkUpdate = async (isActive) => {
    if (selectedServiceIds.size === 0) return;
    setBulkUpdating(true);
    try {
      await servicesAPI.bulkUpdate({
        service_ids: Array.from(selectedServiceIds),
        is_active: isActive
      });
      toast.success(isActive ? 'Services aktiviert' : 'Services deaktiviert');
      clearSelection();
      fetchServices();
    } catch (error) {
      toast.error('Fehler bei der Massenaktion');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleDuplicate = async (serviceId) => {
    setDuplicatingId(serviceId);
    try {
      await servicesAPI.duplicate(serviceId);
      toast.success('Service dupliziert');
      fetchServices();
    } catch (error) {
      toast.error('Fehler beim Duplizieren');
    } finally {
      setDuplicatingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalServices / pageSize));
  const isRefreshing = loading && services.length > 0;

  if (initialLoading) {
    return (
      <div className="flex justify-center py-12" data-testid="services-page-loader">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex items-center justify-between">
        <div>
          {!embedded && (
            <Button
              variant="outline"
              className="mb-3"
              onClick={() => navigate('/settings')}
              data-testid="services-back-to-settings-button"
            >
              ← Zurück zu Einstellungen
            </Button>
          )}
          <h1 className="text-3xl font-bold">Services</h1>
          <p className="text-gray-600 mt-1">Verwalten Sie Ihre Dienstleistungen</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept="application/json"
            id="services-import-file"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              try {
                const text = await file.text();
                const parsed = JSON.parse(text);
                const body = Array.isArray(parsed)
                  ? { services: parsed }
                  : { services: parsed.services || [], service_categories: parsed.service_categories || [] };
                const token = localStorage.getItem('token');
                const res = await fetch(`${API}/services/import-all`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify(body)
                });
                const data = await res.json();
                if (res.ok) {
                  toast.success(`Import: ${data.inserted} neu, ${data.skipped} vorhanden, +${data.categories_inserted || 0} Kategorien`);
                  fetchServices();
                } else {
                  toast.error(data.detail || 'Import fehlgeschlagen');
                }
              } catch (err) {
                toast.error('Ungültige JSON-Datei');
              }
            }}
            data-testid="services-import-file-input"
          />
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API}/services/export-all`, { headers: { Authorization: `Bearer ${token}` } });
                const data = await res.json();
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `strongfit-services-${new Date().toISOString().slice(0, 10)}.json`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success(`${data.count} Dienstleistungen exportiert`);
              } catch (err) {
                toast.error('Export fehlgeschlagen');
              }
            }}
            data-testid="services-export-button"
          >
            Export (Live)
          </Button>
          <Button
            variant="outline"
            onClick={() => document.getElementById('services-import-file')?.click()}
            data-testid="services-import-button"
          >
            Import (Preview)
          </Button>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-service-button" onClick={() => resetForm()}>
              <Plus size={16} className="mr-2" />
              Neuer Service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="service-form-dialog">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Service bearbeiten' : 'Neuen Service erstellen'}</DialogTitle>
              <DialogDescription>
                {editMode ? 'Bearbeiten Sie die Service-Details' : 'Erstellen Sie einen neuen Service'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="service-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung</Label>
                <Input
                  id="description"
                  data-testid="service-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kurze Beschreibung — wird als Tooltip im Customer-Popup angezeigt"
                />
                <p className="text-xs text-gray-500">
                  Wird im Add-on-Popup beim Kunden als Hinweis-Icon angezeigt.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="info_url">🔗 Info-Link (optional)</Label>
                <Input
                  id="info_url"
                  data-testid="service-info-url-input"
                  type="url"
                  value={formData.info_url}
                  onChange={(e) => setFormData({ ...formData, info_url: e.target.value })}
                  placeholder="https://strongfit.ch/services/kryosauna"
                />
                <p className="text-xs text-gray-500">
                  Externer Link zur ausführlichen Beschreibung. Wird als „Mehr erfahren →"-Button im Add-on-Popup gerendert.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration">Dauer (Min)</Label>
                  <Input
                    id="duration"
                    data-testid="service-duration-input"
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Preis (CHF)</Label>
                  <Input
                    id="price"
                    data-testid="service-price-input"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="points_cost">Punkte-Kosten</Label>
                  <Input
                    id="points_cost"
                    data-testid="service-points-input"
                    type="number"
                    value={formData.points_cost}
                    onChange={(e) => setFormData({ ...formData, points_cost: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_participants">Max. Teilnehmer</Label>
                  <Input
                    id="max_participants"
                    data-testid="service-max-participants-input"
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Kategorie</Label>
                <Select
                  value={formData.category || '__NONE__'}
                  onValueChange={(value) => {
                    if (value === '__NEW__') {
                      setNewCategoryDialogOpen(true);
                    } else if (value === '__NONE__') {
                      setFormData({ ...formData, category: '' });
                    } else {
                      setFormData({ ...formData, category: value });
                    }
                  }}
                >
                  <SelectTrigger data-testid="service-category-select">
                    <SelectValue placeholder="Kategorie wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__NONE__" data-testid="service-category-option-none">Keine Kategorie</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name} data-testid={`service-category-option-${cat.id}`}>
                        {cat.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__NEW__" className="text-blue-600 font-medium border-t mt-1" data-testid="service-category-option-new">
                      + Neue Kategorie hinzufügen
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="color">Kalender-Farbe</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    id="color"
                    data-testid="service-color-input"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-24 h-10"
                  />
                  <div 
                    className="flex-1 h-10 rounded border-2"
                    data-testid="service-color-preview"
                    style={{ backgroundColor: formData.color }}
                  />
                  <span className="text-sm text-gray-600">{formData.color}</span>
                </div>
                <p className="text-xs text-gray-500">
                  Diese Farbe wird für Termine im Kalender verwendet (ausser bei Vergangenheit=Rot oder Ausgecheckt=Grün)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tax_rate">MWST-Satz (%)</Label>
                  <Input
                    id="tax_rate"
                    data-testid="service-tax-rate-input"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) })}
                  />
                  <p className="text-xs text-gray-500">
                    Standard: {taxSettings.default_tax_rate}%
                  </p>
                </div>

                <div className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    id="tax_included"
                    data-testid="service-tax-included-toggle"
                    checked={formData.tax_included}
                    onChange={(e) => setFormData({ ...formData, tax_included: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="tax_included" className="cursor-pointer">
                    Preis inkl. MWST
                  </Label>
                </div>
                
                {/* Online Bookable Toggle */}
                <div className="flex items-center gap-2 mt-4 p-3 bg-blue-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="is_online_bookable"
                    data-testid="service-online-bookable-toggle"
                    checked={formData.is_online_bookable}
                    onChange={(e) => setFormData({ ...formData, is_online_bookable: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_online_bookable" className="cursor-pointer">
                    🌐 Online buchbar (Kunden können über Chatbot/Website buchen)
                  </Label>
                </div>
                {!formData.is_online_bookable && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Nur Admin und Trainer können diesen Service buchen
                  </p>
                )}
                
                {/* Training Service Toggle */}
                <div className="flex items-center gap-2 mt-4 p-3 bg-orange-50 rounded-lg col-span-2">
                  <input
                    type="checkbox"
                    id="is_training_service"
                    data-testid="is-training-service-toggle"
                    checked={formData.is_training_service}
                    onChange={(e) => setFormData({ ...formData, is_training_service: e.target.checked })}
                    className="w-4 h-4 accent-orange-500"
                  />
                  <Label htmlFor="is_training_service" className="cursor-pointer">
                    🏋️ Trainings-Service (zählt als Training für Ernährungsziele)
                  </Label>
                </div>
                {formData.is_training_service && (
                  <p className="text-xs text-orange-600 mt-1 col-span-2">
                    ✓ Termine dieses Services aktivieren automatisch erhöhte Kalorien- und Protein-Ziele
                  </p>
                )}
                {!formData.is_training_service && (
                  <p className="text-xs text-gray-500 mt-1 col-span-2">
                    Dieser Service wird nicht als Training gewertet (z.B. Beratung, Massage)
                  </p>
                )}

                {/* Mindbody-Push Toggle */}
                <div className="flex items-center gap-2 mt-4 p-3 bg-rose-50 rounded-lg col-span-2">
                  <input
                    type="checkbox"
                    id="push_to_mindbody"
                    data-testid="service-push-mindbody-toggle"
                    checked={!!formData.push_to_mindbody}
                    onChange={(e) => setFormData({ ...formData, push_to_mindbody: e.target.checked })}
                    className="w-4 h-4 accent-rose-500"
                  />
                  <Label htmlFor="push_to_mindbody" className="cursor-pointer">
                    🔁 Zu Mindbody pushen (Termin erscheint dort als „Reserviert")
                  </Label>
                </div>
                {formData.push_to_mindbody && (
                  <p className="text-xs text-rose-600 mt-1 col-span-2">
                    ✓ Termine dieses Services werden automatisch zu Mindbody synchronisiert (wenn Sync-Richtung „StrongFit → Mindbody" oder „Beide" aktiv ist)
                  </p>
                )}

                {/* EMS-Training Toggle (nur ohne Varianten – sonst regelt es die Varianten-Auswahl) */}
                {formData.variants.length === 0 && (
                <div className="flex items-center gap-2 mt-4 p-3 bg-amber-50 rounded-lg col-span-2">
                  <input
                    type="checkbox"
                    id="is_ems"
                    data-testid="service-is-ems-toggle"
                    checked={!!formData.is_ems}
                    onChange={(e) => setFormData({ ...formData, is_ems: e.target.checked })}
                    className="w-4 h-4 accent-amber-500"
                  />
                  <Label htmlFor="is_ems" className="cursor-pointer">
                    🦾 EMS-Training (Anzug vorbereiten – erscheint in der „EMS-Anzüge"-Liste der App)
                  </Label>
                </div>
                )}

                {/* Physio-Sitzung Toggle (zählt gegen die Verordnung) */}
                <div className="flex items-center gap-2 mt-4 p-3 bg-sky-50 rounded-lg col-span-2">
                  <input
                    type="checkbox"
                    id="is_physio_session"
                    data-testid="service-is-physio-session-toggle"
                    checked={!!formData.is_physio_session}
                    onChange={(e) => setFormData({ ...formData, is_physio_session: e.target.checked })}
                    className="w-4 h-4 accent-sky-500"
                  />
                  <Label htmlFor="is_physio_session" className="cursor-pointer">
                    🩺 Physio-Sitzung (zieht automatisch 1 Sitzung von der Verordnung ab)
                  </Label>
                </div>
                {formData.is_physio_session && (
                  <p className="text-xs text-sky-600 mt-1 col-span-2">
                    ✓ Beim Check-in (und im Nacht-Job um 23:00) wird eine Sitzung von der aktiven Verordnung des Kunden abgezogen
                  </p>
                )}

                {/* Multi-Studio Zuordnung */}
                {studios.length > 0 && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg col-span-2">
                    <Label className="text-sm font-semibold flex items-center gap-2 mb-2">
                      📍 In welchen Studios verfügbar?
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Wähle alle Studios aus, in denen dieser Service gebucht werden kann.
                      Wenn leer → wird automatisch dem Default-Studio zugeordnet.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {studios.map((s) => {
                        const isSelected = (formData.studio_ids || []).includes(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            data-testid={`service-studio-chip-${s.short_name || s.id}`}
                            onClick={() => {
                              const current = formData.studio_ids || [];
                              const next = isSelected
                                ? current.filter((id) => id !== s.id)
                                : [...current, s.id];
                              setFormData({ ...formData, studio_ids: next });
                            }}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition border-2 ${
                              isSelected
                                ? 'bg-amber-500 text-white border-amber-600 shadow'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-amber-400'
                            }`}
                          >
                            {isSelected && '✓ '}{s.short_name || s.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Trainer-Zuordnung (Probetraining-Follow-Up braucht ≥2) */}
                {trainers.length > 0 && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg col-span-2">
                    <Label className="text-sm font-semibold flex items-center gap-2 mb-2">
                      🧑‍🏫 Welche Trainer bieten diesen Service an?
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Für das Probetraining-Follow-Up (2. Gratis-Training bei einem <b>anderen</b> Trainer)
                      müssen mindestens <b>2</b> Trainer zugeordnet sein. Leer = keine Einschränkung bei der
                      normalen Buchung, aber KEIN Follow-Up.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {trainers.map((t) => {
                        const name = `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email || t.id;
                        const isSelected = (formData.trainer_ids || []).includes(t.id);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            data-testid={`service-trainer-chip-${t.id}`}
                            onClick={() => {
                              const current = formData.trainer_ids || [];
                              const next = isSelected
                                ? current.filter((id) => id !== t.id)
                                : [...current, t.id];
                              setFormData({ ...formData, trainer_ids: next });
                            }}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition border-2 ${
                              isSelected
                                ? 'bg-emerald-500 text-white border-emerald-600 shadow'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400'
                            }`}
                          >
                            {isSelected && '✓ '}{name}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs mt-2 font-medium" data-testid="service-trainer-count-hint">
                      {(formData.trainer_ids || []).length} Trainer zugeordnet
                      {(formData.trainer_ids || []).length < 2 && (
                        <span className="text-amber-600"> — für Follow-Up min. 2 nötig</span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Once per customer toggle */}
              <div className="border-t pt-4 mt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Nur einmal pro Kunde buchbar</Label>
                    <p className="text-xs text-gray-500 mt-1">
                      Z.B. für Probetrainings oder Erstberatungen
                    </p>
                  </div>
                  <Switch
                    checked={formData.once_per_customer}
                    data-testid="service-once-per-customer-toggle"
                    onCheckedChange={(checked) => setFormData({ ...formData, once_per_customer: checked })}
                  />
                </div>
                {formData.once_per_customer && (
                  <p className="text-xs text-purple-600 mt-2">
                    ⚠️ Kunden können diesen Service im Portal nur einmal buchen. Admin kann weiterhin buchen.
                  </p>
                )}
              </div>

              {/* Probetraining (Trial) toggle — 09/2026 */}
              <div className="border-t pt-4 mt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Probetraining (Probesession)</Label>
                    <p className="text-xs text-gray-500 mt-1">
                      Markiert diesen Service als Probetraining. Dann darf ein Partner mitgebucht/eingeladen
                      werden – auch neue Personen und selbst wenn nur 1 Platz pro Termin verfügbar ist.
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_trial}
                    data-testid="service-is-trial-toggle"
                    onCheckedChange={(checked) => setFormData({ ...formData, is_trial: checked })}
                  />
                </div>
                {formData.is_trial && (
                  <p className="text-xs text-purple-600 mt-2">
                    ✅ Partner-Mitbuchung erlaubt (inkl. neuer Personen), Kapazitätssperre für Partner wird umgangen.
                  </p>
                )}
              </div>

              {/* 07/2026: Include in Service-Abo Widget */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Im „Service-Abo selbst kombinieren"-Widget anbieten</Label>
                    <p className="text-xs text-gray-500 mt-1">
                      Kunden sehen diesen Service im Shop-Widget zum Bündeln als Abo. Probesessions / Erstberatungen sollten hier deaktiviert bleiben.
                    </p>
                  </div>
                  <Switch
                    checked={formData.include_in_service_abo !== false}
                    data-testid="service-include-in-service-abo-toggle"
                    onCheckedChange={(checked) => setFormData({ ...formData, include_in_service_abo: checked })}
                  />
                </div>
              </div>

              {/* Service Variants Section */}
              <div className="border-t pt-4 mt-2">
                <div className="space-y-3">
                  <div>
                    <Label className="font-medium flex items-center gap-2">
                      🔀 Service-Varianten
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">
                      Für Services mit verschiedenen Ausführungen (z.B. Flex Training kann EMS oder Personal Training sein)
                    </p>
                  </div>
                  
                  {/* Add variant input */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Variante hinzufügen (z.B. EMS)"
                      data-testid="service-variant-input"
                      value={newVariant}
                      onChange={(e) => setNewVariant(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVariant())}
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" onClick={addVariant} disabled={!newVariant.trim()} data-testid="service-variant-add-button">
                      <Plus size={16} />
                    </Button>
                  </div>
                  
                  {/* Show current variants */}
                  {formData.variants.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg">
                      {formData.variants.map((variant, idx) => (
                        <span 
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-blue-200 rounded-full text-sm"
                        >
                          {variant}
                          <button
                            type="button"
                            data-testid={`service-variant-remove-${idx}`}
                            onClick={() => removeVariant(variant)}
                            className="text-gray-400 hover:text-red-500 ml-1"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {formData.variants.length > 0 && (
                    <p className="text-xs text-blue-600">
                      💡 Bei Buchung muss der Kunde eine Variante wählen. Im Kalender wird angezeigt welche Variante gebucht wurde.
                    </p>
                  )}

                  {/* EMS pro Variante */}
                  {formData.variants.length > 0 && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="service-ems-variants-section">
                      <Label className="text-sm font-semibold flex items-center gap-2 mb-2">
                        🦾 Welche Varianten sind EMS? (Anzug vorbereiten)
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {formData.variants.map((variant, idx) => {
                          const active = (formData.ems_variants || []).includes(variant);
                          return (
                            <button
                              type="button"
                              key={idx}
                              data-testid={`service-ems-variant-${idx}`}
                              onClick={() => {
                                const cur = formData.ems_variants || [];
                                const next = active
                                  ? cur.filter((v) => v !== variant)
                                  : [...cur, variant];
                                setFormData({ ...formData, ems_variants: next });
                              }}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                active
                                  ? 'bg-amber-500 text-white border-amber-500'
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300'
                              }`}
                            >
                              {active ? '🦾' : '＋'} {variant}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-amber-700 mt-2">
                        Nur Termine mit einer als EMS markierten Variante erscheinen in der „EMS-Anzüge"-Liste der App.
                      </p>
                    </div>
                  )}

                </div>
              </div>

              {/* Aufpreis & Zusatzzeit pro Variante (branchenabhängig) */}
              {verticalFeatures.variant_pricing && formData.variants.length > 0 && (
                <div className="border-t pt-4 mt-2" data-testid="service-variant-pricing-section">
                  <Label className="font-medium flex items-center gap-2">
                    💰 Aufpreis & Zusatzzeit pro Variante
                  </Label>
                  <p className="text-xs text-gray-500 mt-1 mb-3">
                    Lege je Variante einen Aufpreis (+CHF) und zusätzliche Zeit (+Min) fest. Beides wird bei der Buchung automatisch auf Basispreis und Dauer addiert.
                  </p>
                  <div className="space-y-2">
                    {formData.variants.map((variant, idx) => {
                      const vo = getVO(variant);
                      return (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg" data-testid={`service-vo-row-${idx}`}>
                          <span className="flex-1 text-sm font-medium truncate">{variant}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">+CHF</span>
                            <Input
                              type="number" step="0.01" min="0"
                              data-testid={`service-vo-price-${idx}`}
                              value={vo.price_delta ?? 0}
                              onChange={(e) => setVO(variant, { price_delta: parseFloat(e.target.value) || 0 })}
                              className="w-24 h-8"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">+Min</span>
                            <Input
                              type="number" step="1" min="0"
                              data-testid={`service-vo-duration-${idx}`}
                              value={vo.duration_delta_minutes ?? 0}
                              onChange={(e) => setVO(variant, { duration_delta_minutes: parseInt(e.target.value) || 0 })}
                              className="w-20 h-8"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Preis/Dauer je Mitarbeiter (branchenabhängig) */}
              {verticalFeatures.staff_pricing && (formData.trainer_ids || []).length > 0 && (
                <div className="border-t pt-4 mt-2" data-testid="service-staff-pricing-section">
                  <Label className="font-medium flex items-center gap-2">
                    🧑‍🔧 Preis/Dauer je Mitarbeiter
                  </Label>
                  <p className="text-xs text-gray-500 mt-1 mb-3">
                    Optional: Junior/Senior-Staffelung. Leer lassen = Basiswert des Services gilt.
                  </p>
                  <div className="space-y-2">
                    {(formData.trainer_ids || []).map((tid, idx) => {
                      const t = trainers.find((x) => x.id === tid);
                      const name = t ? (`${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email || tid) : tid;
                      const sp = getSP(tid);
                      return (
                        <div key={tid} className="flex items-center gap-2 p-2 bg-indigo-50 rounded-lg" data-testid={`service-sp-row-${idx}`}>
                          <span className="flex-1 text-sm font-medium truncate">{name}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">CHF</span>
                            <Input
                              type="number" step="0.01" min="0" placeholder="Basis"
                              data-testid={`service-sp-price-${idx}`}
                              value={sp.price ?? ''}
                              onChange={(e) => setSP(tid, { price: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })}
                              className="w-24 h-8"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">Min</span>
                            <Input
                              type="number" step="1" min="0" placeholder="Basis"
                              data-testid={`service-sp-duration-${idx}`}
                              value={sp.duration_minutes ?? ''}
                              onChange={(e) => setSP(tid, { duration_minutes: e.target.value === '' ? null : (parseInt(e.target.value) || 0) })}
                              className="w-20 h-8"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Behandlungsphasen / Einwirkzeit (branchenabhängig: Beauty) */}
              {verticalFeatures.treatment_phases && (
                <div className="border-t pt-4 mt-2" data-testid="service-phases-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium flex items-center gap-2">
                        ⏱️ Behandlung in Phasen (Einwirkzeit)
                      </Label>
                      <p className="text-xs text-gray-500 mt-1">
                        Für Behandlungen mit Pause: Während einer Einwirkzeit ist der Mitarbeiter frei, der Platz bleibt aber belegt.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        data-testid="service-phases-toggle"
                        checked={!!formData.has_treatment_phases}
                        onChange={(e) => setFormData({ ...formData, has_treatment_phases: e.target.checked })}
                        className="w-4 h-4"
                      />
                      Aktiv
                    </label>
                  </div>

                  {formData.has_treatment_phases && (
                    <div className="mt-3 space-y-2">
                      <div className="flex flex-wrap items-center gap-2" data-testid="service-phase-templates">
                        <span className="text-xs text-gray-500">Vorlage:</span>
                        {PHASE_TEMPLATES.map((tpl) => (
                          <button
                            key={tpl.label}
                            type="button"
                            data-testid={`service-phase-template-${tpl.label}`}
                            onClick={() => applyPhaseTemplate(tpl)}
                            className="px-2.5 py-1 rounded-full text-xs font-medium border border-rose-300 text-rose-700 bg-white hover:bg-rose-100 transition-colors"
                          >
                            {tpl.label}
                          </button>
                        ))}
                      </div>
                      {(formData.treatment_phases || []).map((p, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-rose-50 rounded-lg" data-testid={`service-phase-row-${idx}`}>
                          <Input
                            placeholder={`Phase ${idx + 1} (z.B. Auftragen)`}
                            data-testid={`service-phase-name-${idx}`}
                            value={p.name || ''}
                            onChange={(e) => updatePhase(idx, { name: e.target.value })}
                            className="flex-1 h-8"
                          />
                          <div className="flex items-center gap-1">
                            <Input
                              type="number" step="1" min="0"
                              data-testid={`service-phase-min-${idx}`}
                              value={p.duration_minutes ?? 0}
                              onChange={(e) => updatePhase(idx, { duration_minutes: parseInt(e.target.value) || 0 })}
                              className="w-16 h-8"
                            />
                            <span className="text-xs text-gray-500">Min</span>
                          </div>
                          <button
                            type="button"
                            data-testid={`service-phase-busy-${idx}`}
                            onClick={() => updatePhase(idx, { staff_busy: !p.staff_busy })}
                            className={`px-2 py-1 rounded text-xs font-medium border ${p.staff_busy ? 'bg-rose-500 text-white border-rose-500' : 'bg-white text-emerald-700 border-emerald-300'}`}
                          >
                            {p.staff_busy ? 'Mitarbeiter belegt' : 'Einwirkzeit (frei)'}
                          </button>
                          <button
                            type="button"
                            data-testid={`service-phase-remove-${idx}`}
                            onClick={() => removePhase(idx)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <div className="flex items-center justify-between">
                        <Button type="button" variant="outline" size="sm" onClick={addPhase} data-testid="service-phase-add">
                          <Plus size={14} className="mr-1" /> Phase hinzufügen
                        </Button>
                        <span className="text-xs font-medium text-rose-700" data-testid="service-phases-total">
                          Gesamtdauer: {phasesTotal} Min
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Block C: Benötigte Ressourcen (Raum/Gerät) – fix ODER flexibel nach Typ */}
              <div className="border-t pt-4 mt-2" data-testid="service-resources-section">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold text-gray-800">Benötigte Ressourcen</p>
                  <span className="text-xs text-gray-400">optional</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Lege fest, welche Räume/Geräte diese Leistung belegt. Ein Termin ist nur buchbar,
                  wenn alle benötigten Ressourcen gleichzeitig frei sind.
                </p>
                <div className="space-y-2">
                  {(formData.resource_requirements || []).map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-50 border rounded-lg p-2" data-testid={`service-resource-req-${idx}`}>
                      <select
                        className="text-sm border rounded px-2 py-1.5 bg-white"
                        data-testid={`service-resource-req-mode-${idx}`}
                        value={req.resource_type ? 'type' : 'fixed'}
                        onChange={(e) => {
                          if (e.target.value === 'fixed') {
                            updateResourceReq(idx, { resource_type: null, quantity: 1, resource_id: (availableResources[0] || {}).id || '' });
                          } else {
                            updateResourceReq(idx, { resource_id: null, resource_type: resourceTypes[0] || '', quantity: 1 });
                          }
                        }}
                      >
                        <option value="fixed">Feste Ressource</option>
                        <option value="type">Beliebige nach Typ</option>
                      </select>
                      {!req.resource_type ? (
                        <select
                          className="flex-1 text-sm border rounded px-2 py-1.5 bg-white"
                          data-testid={`service-resource-req-fixed-${idx}`}
                          value={req.resource_id || ''}
                          onChange={(e) => updateResourceReq(idx, { resource_id: e.target.value })}
                        >
                          <option value="">— Ressource wählen —</option>
                          {availableResources.map((r) => (
                            <option key={r.id} value={r.id}>{r.name} ({resourceTypeLabel(r.type)})</option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <select
                            className="flex-1 text-sm border rounded px-2 py-1.5 bg-white"
                            data-testid={`service-resource-req-type-${idx}`}
                            value={req.resource_type || ''}
                            onChange={(e) => updateResourceReq(idx, { resource_type: e.target.value })}
                          >
                            <option value="">— Typ wählen —</option>
                            {resourceTypes.map((t) => (
                              <option key={t} value={t}>{resourceTypeLabel(t)}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min={1}
                            className="w-16 text-sm border rounded px-2 py-1.5"
                            data-testid={`service-resource-req-qty-${idx}`}
                            value={req.quantity || 1}
                            onChange={(e) => updateResourceReq(idx, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                            title="Anzahl"
                          />
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => removeResourceReq(idx)}
                        className="text-gray-400 hover:text-red-500 px-1"
                        data-testid={`service-resource-req-remove-${idx}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="service-resource-req-add"
                    onClick={() => addResourceReq(
                      availableResources.length
                        ? { resource_id: availableResources[0].id, resource_type: null, quantity: 1 }
                        : { resource_id: null, resource_type: resourceTypes[0] || '', quantity: 1 }
                    )}
                  >
                    <Plus size={14} className="mr-1" /> Ressource hinzufügen
                  </Button>
                  {availableResources.length === 0 && (
                    <p className="text-xs text-amber-600" data-testid="service-resource-req-empty">
                      Noch keine Ressourcen angelegt. Lege sie unter „Ressourcen" an.
                    </p>
                  )}
                </div>
              </div>


              {/* Service-specific Reminder Settings */}
              <div className="border-t pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="font-medium flex items-center gap-2">
                      🔔 Eigene Terminerinnerung
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">
                      Zusätzliche Erinnerung speziell für diesen Service
                    </p>
                  </div>
                  <Switch
                    checked={formData.reminder_enabled}
                    data-testid="service-reminder-enabled-toggle"
                    onCheckedChange={(checked) => setFormData({ ...formData, reminder_enabled: checked })}
                  />
                </div>
                
                {formData.reminder_enabled && (
                  <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Stunden vorher</Label>
                        <Input
                          type="number"
                          data-testid="service-reminder-hours-input"
                          min="1"
                          max="168"
                          value={formData.reminder_hours_before}
                          onChange={(e) => setFormData({ ...formData, reminder_hours_before: parseInt(e.target.value) || 12 })}
                        />
                      </div>
                      <div className="space-y-2 pt-6">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="reminder_email"
                            data-testid="service-reminder-email-toggle"
                            checked={formData.reminder_email}
                            onChange={(e) => setFormData({ ...formData, reminder_email: e.target.checked })}
                            className="rounded"
                          />
                          <Label htmlFor="reminder_email" className="text-sm">📧 Email</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="reminder_whatsapp"
                            data-testid="service-reminder-whatsapp-toggle"
                            checked={formData.reminder_whatsapp}
                            onChange={(e) => setFormData({ ...formData, reminder_whatsapp: e.target.checked })}
                            className="rounded"
                          />
                          <Label htmlFor="reminder_whatsapp" className="text-sm">💬 WhatsApp</Label>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600">
                      💡 Z.B. 12h vor Probetraining eine Erinnerung senden
                    </p>
                  </div>
                )}
              </div>

              {/* Pre-appointment Information */}
              <div className="border-t pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="font-medium flex items-center gap-2">
                      📋 Vorab-Informationen
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">
                      Zusätzliche Infos X Tage vor dem Termin senden
                    </p>
                  </div>
                  <Switch
                    checked={formData.pre_info_enabled}
                    data-testid="service-pre-info-toggle"
                    onCheckedChange={(checked) => setFormData({ ...formData, pre_info_enabled: checked })}
                  />
                </div>
                
                {formData.pre_info_enabled && (
                  <div className="space-y-3 p-3 bg-amber-50 rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-sm">Tage vorher</Label>
                      <Input
                        type="number"
                        data-testid="service-pre-info-days-input"
                        min="1"
                        max="14"
                        value={formData.pre_info_days_before}
                        onChange={(e) => setFormData({ ...formData, pre_info_days_before: parseInt(e.target.value) || 3 })}
                        className="w-24"
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Inhalt der Vorab-Information</Label>
                        <div className="flex items-center gap-3 flex-wrap">
                          {/* 06/2026: Vorlagen-Dropdown — User kann aus
                              vordefinierten Email-Vorlagen wählen ODER eigenen
                              Text schreiben (wenn '__none__' gewählt ist). */}
                          {preInfoEmailTemplates.length > 0 && (
                            <Select
                              value="__placeholder__"
                              onValueChange={async (tplId) => {
                                if (!tplId || tplId === '__placeholder__') return;
                                if (
                                  formData.pre_info_content?.trim() &&
                                  !window.confirm('Aktueller Text wird durch die gewählte Vorlage ersetzt. Fortfahren?')
                                ) return;
                                const tpl = preInfoEmailTemplates.find((t) => t.id === tplId);
                                if (tpl?.body) {
                                  setFormData({ ...formData, pre_info_content: tpl.body });
                                  toast.success(`📑 Vorlage "${tpl.name}" eingefügt — jetzt deine Anpassungen vornehmen.`);
                                }
                              }}
                            >
                              <SelectTrigger
                                className="h-7 w-auto px-2 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                                data-testid="pre-info-template-selector"
                              >
                                <SelectValue placeholder="📑 Vorlage wählen" />
                              </SelectTrigger>
                              <SelectContent>
                                {preInfoEmailTemplates.map((t) => (
                                  <SelectItem
                                    key={t.id}
                                    value={t.id}
                                    data-testid={`pre-info-template-option-${t.id}`}
                                  >
                                    {t.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <button
                            type="button"
                            onClick={async () => {
                              const fallbackEmail = JSON.parse(localStorage.getItem('user') || '{}')?.email || '';
                              const recipient = window.prompt(
                                'An welche Adresse soll die Test-Email gehen?\n(Leer lassen für deine eigene Admin-Adresse)',
                                fallbackEmail
                              );
                              if (recipient === null) return; // Cancel
                              try {
                                const token = localStorage.getItem('token');
                                const { data } = await axios.post(
                                  `${API}/services/pre-info/test-email`,
                                  {
                                    content: formData.pre_info_content || '',
                                    service_name: formData.name || 'Beispiel-Termin',
                                    days_before: formData.pre_info_days_before || 3,
                                    recipient_email: recipient.trim() || null,
                                  },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                toast.success(`📨 Test-Email an ${data.recipient} gesendet — prüfe dein Postfach.`);
                              } catch (e) {
                                toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
                              }
                            }}
                            className="text-xs text-green-700 hover:underline font-medium"
                            data-testid="pre-info-test-email-btn"
                          >
                            📨 Test-Email senden
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!formData.pre_info_wa_template_name) {
                                toast.error('Bitte zuerst ein WhatsApp-Template auswählen/einreichen.');
                                return;
                              }
                              const phone = window.prompt(
                                'An welche WhatsApp-Nummer soll die Test-Nachricht gehen?\n(mit Ländervorwahl, z.B. +41 79 123 45 67)',
                                ''
                              );
                              if (phone === null || !phone.trim()) return;
                              try {
                                const token = localStorage.getItem('token');
                                const { data } = await axios.post(
                                  `${API}/services/pre-info/test-whatsapp`,
                                  {
                                    phone_number: phone.trim(),
                                    template_name: formData.pre_info_wa_template_name,
                                    template_language: formData.pre_info_wa_template_language || 'de',
                                    template_params: formData.pre_info_wa_template_params || [],
                                    service_name: formData.name || 'Beispiel-Termin',
                                    days_before: formData.pre_info_days_before || 3,
                                  },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                if (data.preview_blocked) {
                                  toast.info(data.message || 'Vorschau-Umgebung: WhatsApp wird blockiert.');
                                } else {
                                  toast.success(`📱 Test-WhatsApp an ${data.recipient} gesendet.`);
                                }
                              } catch (e) {
                                toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
                              }
                            }}
                            className="text-xs text-green-700 hover:underline font-medium"
                            data-testid="pre-info-test-whatsapp-btn"
                          >
                            📱 Test-WhatsApp senden
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const { data } = await axios.get(
                                  `${API}/services/pre-info/email-default`,
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                if (formData.pre_info_content?.trim() && !window.confirm(
                                  'Aktueller Text wird überschrieben. Fortfahren?'
                                )) return;
                                setFormData({ ...formData, pre_info_content: data.html || '' });
                                toast.success('Standard-Text eingefügt — jetzt deine Anpassungen vornehmen.');
                              } catch (e) {
                                toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
                              }
                            }}
                            className="text-xs text-amber-700 hover:underline font-medium"
                            data-testid="pre-info-load-default-btn"
                          >
                            ✨ Standard-Text einfügen
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const { data } = await axios.get(
                                  `${API}/services/pre-info/email-default?variant=analysis`,
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                if (formData.pre_info_content?.trim() && !window.confirm(
                                  'Aktueller Text wird überschrieben. Fortfahren?'
                                )) return;
                                setFormData({ ...formData, pre_info_content: data.html || '' });
                                toast.success('🔬 Analyse-Vorbereitung eingefügt — bei Bedarf anpassen und speichern.');
                              } catch (e) {
                                toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
                              }
                            }}
                            className="text-xs text-purple-700 hover:underline font-medium"
                            data-testid="pre-info-load-analysis-btn"
                          >
                            🔬 Analyse-Vorbereitung einfügen
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const { data } = await axios.post(
                                `${API}/services/pre-info/preview`,
                                {
                                  content: formData.pre_info_content || '',
                                  service_name: formData.name || 'Beispiel-Termin',
                                  days_before: formData.pre_info_days_before || 3,
                                  wa_template_name: formData.pre_info_wa_template_name || null,
                                  wa_template_language: formData.pre_info_wa_template_language || 'de',
                                  wa_template_params: formData.pre_info_wa_template_params || [],
                                },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              setPreInfoPreview(data);
                            } catch (e) {
                              toast.error('Vorschau fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
                            }
                          }}
                          className="text-xs text-blue-600 hover:underline font-medium"
                          data-testid="pre-info-preview-btn"
                        >
                          👁 Vorschau
                        </button>
                        <button
                          type="button"
                          disabled={aiFormatting}
                          onClick={async () => {
                            const current = (formData.pre_info_content || '').trim();
                            if (!current) {
                              toast.error('Bitte zuerst einen Text eingeben, den die KI aufbereiten soll.');
                              return;
                            }
                            try {
                              setAiFormatting(true);
                              const token = localStorage.getItem('token');
                              const { data } = await axios.post(
                                `${API}/services/pre-info/ai-format`,
                                { content: current },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              if (data.html) {
                                setFormData((prev) => ({ ...prev, pre_info_content: data.html }));
                                toast.success('✨ Text von der KI schön aufbereitet — prüfen und speichern.');
                              } else {
                                toast.error('KI lieferte kein Ergebnis.');
                              }
                            } catch (e) {
                              toast.error('KI-Aufbereitung fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
                            } finally {
                              setAiFormatting(false);
                            }
                          }}
                          className="text-xs text-emerald-700 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="pre-info-ai-format-btn"
                        >
                          {aiFormatting ? '✨ KI formatiert…' : '✨ KI: Schön formatieren'}
                        </button>
                        </div>
                      </div>

                      <SimpleRichTextEditor
                        ref={preInfoEditorRef}
                        value={formData.pre_info_content}
                        onChange={(html) => setFormData({ ...formData, pre_info_content: html })}
                        placeholder="z.B. Bitte nüchtern erscheinen, ausreichend Wasser trinken …"
                        dataTestId="service-pre-info-content"
                      />

                      {/* Platzhalter-Pills */}
                      <div className="flex flex-wrap gap-1.5 pt-2">
                        <span className="text-xs text-amber-800 font-medium mr-1">Einfügen:</span>
                        {PRE_INFO_PLACEHOLDERS.map(({ key, label }) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              const editor = preInfoEditorRef.current;
                              if (editor) {
                                editor.focus();
                                document.execCommand('insertText', false, `{${key}}`);
                                setFormData((prev) => ({ ...prev, pre_info_content: editor.innerHTML }));
                              }
                            }}
                            className="text-[11px] px-2 py-1 rounded-full bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
                            title={`{${key}}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* WhatsApp-Template-Auswahl
                       Meta erlaubt proaktive Nachrichten (>24h) NUR via approved Template.
                       Email läuft mit dem Rich-Text oben — WhatsApp braucht hier explizit
                       ein approved Template aus dem Meta-Manager. */}
                    <div className="border-t pt-4 mt-2 space-y-3">
                      <div>
                        <Label className="font-medium flex items-center gap-2">
                          💬 WhatsApp-Template
                        </Label>
                        <p className="text-[11px] text-gray-500 mt-1">
                          Meta verlangt für proaktive WhatsApp-Nachrichten ein zuvor genehmigtes Template. 
                          Wenn leer, wird die Vorab-Info <strong>nur per Email</strong> gesendet.
                        </p>
                      </div>

                      {/* Quick-Seed: erstellt vorgefertigtes Vorab-Info-Template
                         direkt bei Meta — der User muss nicht manuell ins
                         Business-Manager-UI. */}
                      <button
                        type="button"
                        className="text-xs px-3 py-1.5 rounded-md border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 inline-flex items-center gap-1.5"
                        data-testid="seed-pre-info-template-btn"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const { data } = await axios.get(
                              `${API}/services/pre-info/wa-template-default`,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setSeedDialog({
                              name: data.name || 'vorab_info_v1',
                              language: data.language || 'de',
                              body_text: data.body_text || '',
                              footer_text: data.footer_text || '',
                              param_keys: data.param_keys || [],
                            });
                          } catch (e) {
                            toast.error('Konnte Standard-Vorlage nicht laden: ' + (e.response?.data?.detail || e.message));
                          }
                        }}
                      >
                        📤 Vorab-Info-Template vor Submit bearbeiten & einreichen
                      </button>
                      <button
                        type="button"
                        className="text-xs px-3 py-1.5 rounded-md border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 inline-flex items-center gap-1.5 ml-2"
                        data-testid="seed-pre-info-analysis-template-btn"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const { data } = await axios.get(
                              `${API}/services/pre-info/wa-template-default?variant=analysis`,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setSeedDialog({
                              name: data.name || 'analyse_vorbereitung_v1',
                              language: data.language || 'de',
                              body_text: data.body_text || '',
                              footer_text: data.footer_text || '',
                              param_keys: data.param_keys || [],
                            });
                          } catch (e) {
                            toast.error('Konnte Analyse-Vorlage nicht laden: ' + (e.response?.data?.detail || e.message));
                          }
                        }}
                      >
                        🔬 Analyse-WhatsApp-Vorlage vorbereiten & einreichen
                      </button>

                      <Select
                        value={formData.pre_info_wa_template_name || '__none__'}
                        onValueChange={(v) => setFormData({
                          ...formData,
                          pre_info_wa_template_name: v === '__none__' ? '' : v,
                          pre_info_wa_template_language: v === '__none__'
                            ? 'de'
                            : (waTemplates.find((t) => t.name === v)?.language || 'de'),
                          pre_info_wa_template_params: [],
                        })}
                      >
                        <SelectTrigger data-testid="pre-info-wa-template-select" className="bg-white">
                          <SelectValue placeholder="Template auswählen …" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Kein WhatsApp-Versand —</SelectItem>
                          {waTemplates.map((t) => (
                            <SelectItem key={t.id || t.name} value={t.name}>
                              {t.name} <span className="text-gray-400 text-xs">({t.language})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {formData.pre_info_wa_template_name && (
                        <div className="bg-green-50 border border-green-200 rounded p-3 space-y-2">
                          <div className="text-[11px] uppercase text-green-700 font-semibold">Template-Body (aus Meta)</div>
                          <pre className="text-xs whitespace-pre-wrap font-sans text-gray-700">
{getTemplateBodyPreview(formData.pre_info_wa_template_name)}
                          </pre>

                          {getTemplateBodyVariableCount(formData.pre_info_wa_template_name) > 0 && (
                            <div className="space-y-2 pt-2">
                              <div className="text-[11px] uppercase text-green-700 font-semibold">
                                Variablen-Mapping ({getTemplateBodyVariableCount(formData.pre_info_wa_template_name)} {' '}
                                Variable(n))
                              </div>
                              {Array.from({ length: getTemplateBodyVariableCount(formData.pre_info_wa_template_name) }).map((_, i) => {
                                const current = formData.pre_info_wa_template_params?.[i] || '';
                                return (
                                  <div key={i} className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-gray-500 w-12">{`{{${i + 1}}}`}</span>
                                    <Select
                                      value={current || '__empty__'}
                                      onValueChange={(v) => {
                                        const next = [...(formData.pre_info_wa_template_params || [])];
                                        while (next.length < getTemplateBodyVariableCount(formData.pre_info_wa_template_name)) next.push('');
                                        next[i] = v === '__empty__' ? '' : v;
                                        setFormData({ ...formData, pre_info_wa_template_params: next });
                                      }}
                                    >
                                      <SelectTrigger data-testid={`pre-info-wa-param-${i}`} className="bg-white flex-1">
                                        <SelectValue placeholder="Platzhalter wählen …" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="__empty__">— Leer lassen —</SelectItem>
                                        {PRE_INFO_PLACEHOLDERS.map(({ key, label }) => (
                                          <SelectItem key={key} value={key}>{label} <span className="text-gray-400 text-xs ml-1">{`{${key}}`}</span></SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-amber-700">
                      💡 Z.B. für Analyse-Termine: Vorbereitungs-Anweisungen 3 Tage vorher
                    </p>
                  </div>
                )}
              </div>

              {/* Warmup Time Section — 07/2026: entfernt aus Service-Editor.
                  Warmup-Zeiten werden jetzt zentral in
                  Einstellungen → Add-ons → Aufwärm-Zeiten pro Service gepflegt.
                  Grund: einheitliche Config-Stelle, kein Duplikat. Das Feld
                  `warmup_time_minutes` bleibt am Service-Dokument bestehen
                  und wird von addon_availability_service.py gelesen. */}
              <div className="border-t pt-4 mt-2 bg-blue-50 rounded p-3 text-sm text-blue-800">
                <p className="font-medium">⏱️ Aufwärmzeit</p>
                <p className="text-xs text-blue-700 mt-1">
                  Warmup-Zeiten für Add-on-Puffer-Berechnungen findest du zentral unter{' '}
                  <a
                    href="/settings/advanced?tab=addons"
                    className="underline hover:text-blue-900"
                    data-testid="warmup-config-link"
                  >
                    Einstellungen → Add-ons → Aufwärm-Zeiten pro Service
                  </a>
                  . Diese wirken NUR auf die Add-on-Empfehlungen, nicht auf den normalen Slot-Filter.
                </p>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-service-button">
                {editMode ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>


        {/* Dialog: Neue Kategorie hinzufügen */}
        <Dialog open={newCategoryDialogOpen} onOpenChange={setNewCategoryDialogOpen}>
          <DialogContent data-testid="new-category-dialog">
            <DialogHeader>
              <DialogTitle>Neue Kategorie hinzufügen</DialogTitle>
              <DialogDescription>
                Erstellen Sie eine neue Service-Kategorie
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-category">Kategorie-Name</Label>
                <Input
                  id="new-category"
                  data-testid="new-category-input"
                  placeholder="z.B. Personal Training"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddNewCategory()}
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleAddNewCategory} className="flex-1" data-testid="new-category-create-button">
                  Erstellen
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setNewCategoryDialogOpen(false);
                    setNewCategoryName('');
                  }}
                  className="flex-1"
                  data-testid="new-category-cancel-button"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Seed-Dialog: Vorab-Info-Template bearbeiten und bei Meta einreichen */}
        <Dialog open={!!seedDialog} onOpenChange={(o) => { if (!o) setSeedDialog(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>WhatsApp-Vorlage bearbeiten</DialogTitle>
              <DialogDescription>
                Passe den Text an deinen Studio-Tonfall an, bevor er bei Meta zur Genehmigung eingereicht wird.
                Verwende <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>, … für Variablen.
              </DialogDescription>
            </DialogHeader>
            {seedDialog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Template-Name</Label>
                    <Input
                      value={seedDialog.name}
                      onChange={(e) => setSeedDialog({ ...seedDialog, name: e.target.value })}
                      placeholder="z.B. vorab_info_v1"
                      data-testid="seed-template-name"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Nur Kleinbuchstaben, Zahlen und Unterstriche. Bei jedem Body-Change bitte neuen Namen wählen (z.B. <code>v2</code>).
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm">Sprache</Label>
                    <Input
                      value={seedDialog.language}
                      onChange={(e) => setSeedDialog({ ...seedDialog, language: e.target.value })}
                      placeholder="de"
                      data-testid="seed-template-language"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Body</Label>
                    <button
                      type="button"
                      disabled={waFormatting}
                      onClick={async () => {
                        const current = (seedDialog.body_text || '').trim();
                        if (!current) {
                          toast.error('Bitte zuerst einen Text eingeben, den die KI aufbereiten soll.');
                          return;
                        }
                        try {
                          setWaFormatting(true);
                          const token = localStorage.getItem('token');
                          const { data } = await axios.post(
                            `${API}/whatsapp/format-text`,
                            { text: current },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          if (data.formatted_text) {
                            setSeedDialog((prev) => ({ ...prev, body_text: data.formatted_text }));
                            toast.success('✨ WhatsApp-Text von der KI schön aufbereitet — Variablen bleiben erhalten.');
                          } else {
                            toast.error('KI lieferte kein Ergebnis.');
                          }
                        } catch (e) {
                          toast.error('KI-Aufbereitung fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
                        } finally {
                          setWaFormatting(false);
                        }
                      }}
                      className="text-xs text-emerald-700 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="seed-template-ai-format-btn"
                    >
                      {waFormatting ? '✨ KI formatiert…' : '✨ KI: Schön formatieren'}
                    </button>
                  </div>
                  <textarea
                    value={seedDialog.body_text}
                    onChange={(e) => setSeedDialog({ ...seedDialog, body_text: e.target.value })}
                    className="w-full p-2 border rounded-md text-sm font-mono min-h-[260px] bg-white"
                    data-testid="seed-template-body"
                  />
                </div>

                <div>
                  <Label className="text-sm">Footer (optional)</Label>
                  <Input
                    value={seedDialog.footer_text}
                    onChange={(e) => setSeedDialog({ ...seedDialog, footer_text: e.target.value })}
                    placeholder="StrongFit · Vorab-Information"
                    data-testid="seed-template-footer"
                  />
                </div>

                <div>
                  <Label className="text-sm">Variablen-Mapping (Reihenfolge {'{{1}}, {{2}}, …'})</Label>
                  <div className="space-y-1.5 pt-1">
                    {(seedDialog.param_keys || []).map((k, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-500 w-12">{`{{${i + 1}}}`}</span>
                        <Select
                          value={k}
                          onValueChange={(v) => {
                            const next = [...(seedDialog.param_keys || [])];
                            next[i] = v;
                            setSeedDialog({ ...seedDialog, param_keys: next });
                          }}
                        >
                          <SelectTrigger className="bg-white flex-1" data-testid={`seed-param-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PRE_INFO_PLACEHOLDERS.map(({ key, label }) => (
                              <SelectItem key={key} value={key}>{label} <span className="text-gray-400 text-xs ml-1">{`{${key}}`}</span></SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <button
                          type="button"
                          onClick={() => {
                            const next = (seedDialog.param_keys || []).filter((_, idx) => idx !== i);
                            setSeedDialog({ ...seedDialog, param_keys: next });
                          }}
                          className="text-xs text-red-500 hover:underline"
                          title="Variable entfernen"
                        >Entfernen</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setSeedDialog({
                        ...seedDialog,
                        param_keys: [...(seedDialog.param_keys || []), 'first_name'],
                      })}
                      className="text-xs text-blue-600 hover:underline mt-1"
                    >+ Variable hinzufügen</button>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => setSeedDialog(null)}>Abbrechen</Button>
                  <Button
                    data-testid="seed-template-submit"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const { data } = await axios.post(
                          `${API}/services/pre-info/seed-wa-template`,
                          {
                            name: seedDialog.name,
                            language: seedDialog.language,
                            body_text: seedDialog.body_text,
                            footer_text: seedDialog.footer_text,
                            param_keys: seedDialog.param_keys,
                          },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (data.status === 'ALREADY_EXISTS') {
                          toast.success(`Template "${data.template_name}" existiert bereits — wähle einen neuen Namen, wenn du den Body ändern willst.`);
                        } else {
                          toast.success(`Template "${data.template_name}" eingereicht — Status: ${data.status}`);
                        }
                        setFormData((prev) => ({
                          ...prev,
                          pre_info_wa_template_name: data.template_name,
                          pre_info_wa_template_language: seedDialog.language || 'de',
                          pre_info_wa_template_params: data.param_keys || [],
                        }));
                        try {
                          const r = await axios.get(
                            `${API}/whatsapp/templates?status=APPROVED&limit=200`,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setWaTemplates(Array.isArray(r.data?.data) ? r.data.data : []);
                        } catch (_) {}
                        setSeedDialog(null);
                      } catch (e) {
                        toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
                      }
                    }}
                  >
                    📤 Bei Meta einreichen
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Vorab-Info Vorschau-Dialog */}
        <Dialog open={!!preInfoPreview} onOpenChange={(o) => { if (!o) setPreInfoPreview(null); }}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Vorab-Info Vorschau</DialogTitle>
              <DialogDescription>
                So sieht die Vorab-Info mit Beispieldaten beim Kunden aus (Max Muster, in {formData.pre_info_days_before || 3} Tagen).
              </DialogDescription>
            </DialogHeader>
            {preInfoPreview && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold mb-2 text-gray-700">📧 Email</h3>
                  <div className="border rounded p-3 bg-white max-h-[420px] overflow-auto"
                       dangerouslySetInnerHTML={{ __html: preInfoPreview.email_html }} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-2 text-gray-700">💬 WhatsApp</h3>
                  {preInfoPreview.whatsapp_template_preview ? (
                    <div className="border rounded p-3 bg-green-50 text-sm whitespace-pre-wrap font-sans max-h-[420px] overflow-auto">
                      <div className="text-[11px] uppercase text-green-700 font-semibold mb-1">
                        Template: <code>{preInfoPreview.whatsapp_template_preview.template_name}</code>
                        {preInfoPreview.whatsapp_template_preview.status && (
                          <span className="ml-2 text-green-800 font-normal normal-case">[{preInfoPreview.whatsapp_template_preview.status}]</span>
                        )}
                      </div>
                      <div className="text-gray-800">{preInfoPreview.whatsapp_template_preview.rendered_body || preInfoPreview.whatsapp_template_preview.raw_body || preInfoPreview.whatsapp_template_preview.error}</div>
                      {preInfoPreview.whatsapp_template_preview.params?.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-green-200 text-[11px] text-green-900">
                          {preInfoPreview.whatsapp_template_preview.params.map((p) => (
                            <div key={p.index}><code>{`{{${p.index}}}`}</code> = <code>{`{${p.key}}`}</code> → <strong>{p.value || '—'}</strong></div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <pre className="border rounded p-3 bg-gray-50 text-sm whitespace-pre-wrap font-sans max-h-[420px] overflow-auto text-gray-500">
⚠️ Kein WhatsApp-Template gewählt — Vorab-Info wird NUR per Email gesendet.
                    </pre>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>

      <div className="flex flex-col gap-3" data-testid="services-toolbar">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Service suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="services-search-input"
            />
          </div>
          <div className="w-full lg:w-64">
            <Select value={categoryFilter} onValueChange={(value) => setCategoryFilter(value)}>
              <SelectTrigger data-testid="services-category-filter">
                <SelectValue placeholder="Kategorie filtern..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="services-category-filter-all">Alle Kategorien</SelectItem>
                <SelectItem value="__NONE__" data-testid="services-category-filter-none">Ohne Kategorie</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.name} data-testid={`services-category-filter-${cat.id}`}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {studios.length > 1 && (
            <div className="w-full lg:w-56">
              <Select value={studioFilter} onValueChange={setStudioFilter}>
                <SelectTrigger data-testid="services-studio-filter">
                  <SelectValue placeholder="Studio filtern..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">📍 Alle Studios</SelectItem>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`services-studio-filter-${s.short_name || s.id}`}>
                      📍 {s.short_name || s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setStudioFilter('all');
              }}
              data-testid="services-clear-filters-button"
            >
              Filter zurücksetzen
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
          <div className="flex items-center gap-2" data-testid="services-total-count">
            Gesamt: {totalServices} Services
          </div>
          {isRefreshing && (
            <div className="text-xs text-gray-500" data-testid="services-refreshing-indicator">
              Aktualisiere Liste...
            </div>
          )}
        </div>
      </div>

      {selectedCount > 0 && (
        <Card className="border-dashed" data-testid="services-bulk-actions">
          <CardContent className="py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="text-sm text-gray-700" data-testid="services-bulk-count">
              {selectedCount} Service{selectedCount !== 1 ? 's' : ''} ausgewählt
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => handleBulkUpdate(true)}
                disabled={bulkUpdating}
                data-testid="services-bulk-activate"
              >
                Aktivieren
              </Button>
              <Button
                variant="outline"
                onClick={() => handleBulkUpdate(false)}
                disabled={bulkUpdating}
                data-testid="services-bulk-deactivate"
              >
                Deaktivieren
              </Button>
              <Button
                variant="ghost"
                onClick={clearSelection}
                data-testid="services-bulk-clear"
              >
                Auswahl löschen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" data-testid="services-header-select">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    data-testid="services-select-all"
                  />
                </TableHead>
                <TableHead className="w-24" data-testid="services-header-order">Reihenfolge</TableHead>
                <TableHead data-testid="services-header-name">Name</TableHead>
                <TableHead data-testid="services-header-description">Beschreibung</TableHead>
                <TableHead data-testid="services-header-duration">Dauer</TableHead>
                <TableHead data-testid="services-header-price">Preis</TableHead>
                <TableHead data-testid="services-header-points">Punkte</TableHead>
                <TableHead data-testid="services-header-max-participants">Max. TN</TableHead>
                <TableHead data-testid="services-header-variants">Varianten</TableHead>
                <TableHead data-testid="services-header-category">Kategorie</TableHead>
                <TableHead data-testid="services-header-active">Aktiv</TableHead>
                <TableHead data-testid="services-header-online">Online</TableHead>
                <TableHead data-testid="services-header-training">Training</TableHead>
                <TableHead data-testid="services-header-actions">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-8 text-gray-500" data-testid="services-loading-row">
                    Services werden geladen...
                  </TableCell>
                </TableRow>
              ) : services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-8 text-gray-500" data-testid="services-empty-state">
                    Keine Services vorhanden
                  </TableCell>
                </TableRow>
              ) : (
                services
                  .filter((service) => {
                    if (studioFilter === 'all') return true;
                    const sids = service.studio_ids && service.studio_ids.length > 0
                      ? service.studio_ids
                      : (service.studio_id ? [service.studio_id] : []);
                    return sids.includes(studioFilter);
                  })
                  .map((service, index) => (
                  <TableRow key={service.id} data-testid={`service-row-${index}`} className={service.is_active === false ? 'opacity-60' : ''}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedServiceIds.has(service.id)}
                        onChange={() => toggleServiceSelection(service.id)}
                        data-testid={`service-select-${service.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`service-move-up-${index}`}
                          onClick={() => handleMoveService(service.id, 'up')}
                          disabled={index === 0 && currentPage === 1}
                          className="p-1 h-7 w-7"
                          title="Nach oben"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`service-move-down-${index}`}
                          onClick={() => handleMoveService(service.id, 'down')}
                          disabled={index === services.length - 1 && currentPage === totalPages}
                          className="p-1 h-7 w-7"
                          title="Nach unten"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium" data-testid={`service-name-${service.id}`}>{service.name}</TableCell>
                    <TableCell data-testid={`service-description-${service.id}`}>{service.description || '-'}</TableCell>
                    <TableCell data-testid={`service-duration-${service.id}`}>{service.duration_minutes} Min</TableCell>
                    <TableCell data-testid={`service-price-${service.id}`}>{calculatePriceDisplay(service)}</TableCell>
                    <TableCell data-testid={`service-points-${service.id}`}>{service.points_cost || 0} Pkt</TableCell>
                    <TableCell data-testid={`service-max-participants-${service.id}`}>{service.max_participants || '-'}</TableCell>
                    <TableCell data-testid={`service-variants-${service.id}`}>
                      {service.variants && service.variants.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {service.variants.map((v, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs" data-testid={`service-variant-chip-${service.id}-${i}`}>
                              {v}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400" data-testid={`service-variant-none-${service.id}`}>-</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`service-category-${service.id}`}>{service.category || '-'}</TableCell>
                    <TableCell data-testid={`service-active-${service.id}`}>
                      {service.is_active === false ? (
                        <span className="text-gray-500" data-testid={`service-active-false-${service.id}`}>Inaktiv</span>
                      ) : (
                        <span className="text-green-600" data-testid={`service-active-true-${service.id}`}>Aktiv</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`service-online-${service.id}`}>
                      {service.is_online_bookable !== false ? (
                        <span className="text-green-600" title="Kunden können online buchen" data-testid={`service-online-enabled-${service.id}`}>🌐</span>
                      ) : (
                        <span className="text-gray-400" title="Nur Admin/Trainer" data-testid={`service-online-disabled-${service.id}`}>🔒</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`service-training-${service.id}`}>
                      {service.is_training_service !== false ? (
                        <span className="text-orange-500" title="Zählt als Training für Ernährungsziele" data-testid={`service-training-enabled-${service.id}`}>🏋️</span>
                      ) : (
                        <span className="text-gray-400" title="Kein Training" data-testid={`service-training-disabled-${service.id}`}>–</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`edit-service-${index}`}
                          onClick={() => handleEdit(service)}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`duplicate-service-${index}`}
                          onClick={() => handleDuplicate(service.id)}
                          disabled={duplicatingId === service.id}
                        >
                          <Copy size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`delete-service-${index}`}
                          onClick={() => handleDelete(service.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t px-4 py-3" data-testid="services-pagination">
            <div className="text-sm text-gray-600" data-testid="services-pagination-info">
              Seite {currentPage} von {totalPages} • {totalServices} Services
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                data-testid="services-prev-page"
              >
                Zurück
              </Button>
              <Button
                variant="outline"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
                data-testid="services-next-page"
              >
                Weiter
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServicesPage;
